import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  TextField,
  FormControl,
  FormLabel,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  CircularProgress,
  Divider,
  Popover,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Checkbox } from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  CalendarToday as CalendarTodayIcon,
  AccessTime as AccessTimeIcon,
  AttachFile as AttachFileIcon,
  InsertDriveFile as InsertDriveFileIcon,
  PictureAsPdf as PictureAsPdfIcon,
  TableChart as TableChartIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIndicatorIcon,
} from '@mui/icons-material';
import { API_CONFIG, STORAGE_KEYS, taskFormConfig, FormField, API_ENDPOINTS } from '../helpers/constants';
import { useToast } from '../contexts/ToastContext';
import  TaskDetailsView  from './TaskDetailsView';
import { formatLocalDateTime, formatRelativeTime } from '../helpers/dateUtils';

interface TaskAssignmentPanelProps {
  open: boolean;
  onClose: () => void;
  channelId?: string;
  threadId?: string;
  messageId?: string;
  onTaskAssigned?: () => void;
  tasks?: any[]; // Add tasks prop
  /** When true, parent is still fetching tasks; show loading instead of assign form */
  tasksLoading?: boolean;
}

interface Member {
  user_id: string;
  email_id: string;
  name: string;
  user_role: string;
  membership_role: string;
}

const TaskAssignmentPanel: React.FC<TaskAssignmentPanelProps> = ({ 
  open, 
  onClose, 
  channelId,
  threadId,
  messageId,
  onTaskAssigned,
  tasks,
  tasksLoading = false,
}) => {
  // Theme for dark mode support (panel background, borders, form controls)
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Initialize form data based on configuration
  const initializeFormData = () => {
    const initialData: Record<string, any> = {};
    taskFormConfig.fields.forEach(field => {
      if (field.type === 'dynamic-list') {
        initialData[field.id] = []; // Initialize as empty array for checklist
      } else if (field.type === 'file-upload') {
        initialData[field.id] = [];
      } else if (field.id === 'priority') {
        initialData[field.id] = 'Medium'; // Set default value for priority
      } else {
        initialData[field.id] = '';
      }
    });
    // Add statusComment field for status changes
    initialData.statusComment = '';
    return initialData;
  };

  const [formData, setFormData] = useState<Record<string, any>>(initializeFormData());
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  
  const [members, setMembers] = useState<Member[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const toast = useToast();
  
  // Checklist state
  const [showChecklistInput, setShowChecklistInput] = useState(false);
  const [checklistInputValue, setChecklistInputValue] = useState('');
  const [editingChecklistIndex, setEditingChecklistIndex] = useState<number | null>(null);
  const [editingChecklistValue, setEditingChecklistValue] = useState('');
  const [draggedItemIndex, setDraggedItemIndex] = useState<number | null>(null);
  const [isEditingMode, setIsEditingMode] = useState(false);
  const [isChannelAdmin, setIsChannelAdmin] = useState(false);
  
  // Checklist validation error state
  const [checklistError, setChecklistError] = useState<string | null>(null);
  
  // Permission check functions for checklist items
  const isCurrentUserAssignee = () => {
    if (!tasks || tasks.length === 0) return false;
    
    try {
      const storedUserData = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
      if (!storedUserData) return false;
      
      const parsedUserData = JSON.parse(storedUserData);
      const currentUserId = parsedUserData.user_id;
      
      // Always use 0th index for task details
      const task = tasks[0];
      return currentUserId === task.task_details.assignee.id;
    } catch (error) {
      console.error('Error parsing user data:', error);
      return false;
    }
  };

  const isCurrentUserCreator = () => {
    if (!tasks || tasks.length === 0) return false;
    
    try {
      const storedUserData = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
      if (!storedUserData) return false;
      
      const parsedUserData = JSON.parse(storedUserData);
      const currentUserId = parsedUserData.user_id;
      
      // Always use 0th index for task details
      const task = tasks[0];
      return currentUserId === task.task_details.created_by.id;
    } catch (error) {
      console.error('Error parsing user data:', error);
      return false;
    }
  };

  const canEditChecklist = () => {
    return isCurrentUserAssignee() || isCurrentUserCreator();
  };

  const canDeleteChecklist = () => {
    return isCurrentUserAssignee() || isCurrentUserCreator();
  };

  // Permission check functions for task actions
  const isCurrentUserChannelAdmin = async () => {
    if (!tasks || tasks.length === 0) return false;
    
    try {
      const storedUserData = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
      if (!storedUserData) return false;
      
      const parsedUserData = JSON.parse(storedUserData);
      const currentUserId = parsedUserData.user_id;
      
      // Always use 0th index for task details
      const task = tasks[0];
      const channelId = task.context.channel.id;
      
      // Import channelService to check if user is channel admin
      const { default: channelService } = await import('../services/channelService');
      
      try {
        const members = await channelService.getChannelMembersNoPaging(channelId);
        const currentUserMember = members.find(member => member.id === currentUserId);
        
        // Check if user has admin role in the channel
        return currentUserMember?.role === 'admin' || currentUserMember?.role === 'Admin';
      } catch (error) {
        console.error('Error fetching channel members:', error);
        return false;
      }
    } catch (error) {
      console.error('Error checking channel admin status:', error);
      return false;
    }
  };

  const isCurrentUserGlobalAdmin = () => {
    try {
      const storedUserData = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
      if (!storedUserData) return false;
      
      const parsedUserData = JSON.parse(storedUserData);
      const currentUserRole = parsedUserData.role;
      
      return currentUserRole === 'admin';
    } catch (error) {
      console.error('Error checking global admin status:', error);
      return false;
    }
  };

  const canReassignTask = () => {
    return isCurrentUserAssignee() || isCurrentUserCreator() || isCurrentUserGlobalAdmin() || isChannelAdmin;
  };



  const canChangeTaskStatus = () => {
    return isCurrentUserAssignee();
  };

  // Check channel admin status when component mounts
  useEffect(() => {
    const checkChannelAdminStatus = async () => {
      if (tasks && tasks.length > 0) {
        try {
          const channelAdminStatus = await isCurrentUserChannelAdmin();
          setIsChannelAdmin(channelAdminStatus);
        } catch (error) {
          console.error('Error checking channel admin status:', error);
          setIsChannelAdmin(false);
        }
      }
    };

    checkChannelAdminStatus();
  }, [tasks]);
  
  // Calendar state
  const [calendarAnchorEl, setCalendarAnchorEl] = useState<HTMLElement | null>(null);
  const [calendarPosition, setCalendarPosition] = useState<{ top: number; left: number } | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  // Reassignment calendar state
  const [reassignCalendarAnchorEl, setReassignCalendarAnchorEl] = useState<HTMLElement | null>(null);
  const [reassignCalendarPosition, setReassignCalendarPosition] = useState<{ top: number; left: number } | null>(null);
  const [reassignSelectedDate, setReassignSelectedDate] = useState<Date | null>(null);

  // File upload state
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [showFileLimitWarning, setShowFileLimitWarning] = useState(false);

  // Removed unused states for assigned task since we now show task management interface

  // New states for task management
  const [isReassigning, setIsReassigning] = useState(false);
  const [isChangingStatus, setIsChangingStatus] = useState(false);
  const [taskDetailsExpanded, setTaskDetailsExpanded] = useState(true); // Default to expanded
  const [taskHistoryExpanded, setTaskHistoryExpanded] = useState(true); // Default to expanded

  // Function to format date for API (convert MM/DD/YYYY to YYYY-MM-DD)
  const formatDateForAPI = (dateString: string): string => {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };



  // Function to format time for display (convert YYYY-MM-DD HH:MM:SS to HH:MM AM/PM)
  const formatTimeForDisplay = (dateString: string): string => {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      
      const hours = date.getHours();
      const minutes = date.getMinutes();
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours % 12 || 12;
      
      return `${displayHours}:${String(minutes).padStart(2, '0')} ${ampm}`;
    } catch (error) {
      console.error('Error formatting time for display:', error);
      return '';
    }
  };

  // Function to format date and time for API (convert to YYYY-MM-DD HH:MM:SS +0530 format)
  const formatDateTimeForAPI = (dateString: string, timeString: string): string => {
    if (!dateString || !timeString) return '';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      
      // Parse time string (e.g., "11:15 AM")
      const time = new Date(`2000-01-01 ${timeString}`);
      if (isNaN(time.getTime())) return '';
      
      const hours = String(time.getHours()).padStart(2, '0');
      const minutes = String(time.getMinutes()).padStart(2, '0');
      const seconds = '00';
      
      // Add timezone offset (+0530 for IST)
      const timezone = '+0530';
      
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds} ${timezone}`;
    } catch (error) {
      console.error('Error formatting date and time:', error);
      return '';
    }
  };

  // Generate time options in 15-minute intervals
  const generateTimeOptions = () => {
    const options = [];
    const now = new Date();
    const selectedDateObj = selectedDate ? new Date(selectedDate) : null;
    const isToday = selectedDateObj && selectedDateObj.toDateString() === now.toDateString();

    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const time = new Date();
        time.setHours(hour, minute, 0, 0);
        
        // If today is selected, only show future times
        if (isToday && time <= now) {
          continue;
        }

        const timeString = time.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
        
        options.push({
          value: timeString,
          label: timeString
        });
      }
    }
    
    return options;
  };

  // Fetch members when panel opens
  useEffect(() => {
    if (open && channelId) {
      fetchMembers();
    }
  }, [open, channelId]);

  // Debug calendar state changes
  useEffect(() => {
    // Calendar state tracking removed for production
  }, [calendarAnchorEl, calendarPosition, isReassigning, isChangingStatus]);



  // Ensure checklist data is loaded when reassigning
  useEffect(() => {
    if (isReassigning && tasks && tasks.length > 0) {
      const task = tasks[0];
      if (task.checklists && Array.isArray(task.checklists)) {
        const checklistData = task.checklists.map((item: any) => item.text || item).filter(Boolean);
        if (checklistData.length > 0) {
          setFormData(prev => ({
            ...prev,
            checklist: checklistData
          }));
        } else {
          setFormData(prev => ({
            ...prev,
            checklist: []
          }));
        }
      } else {
        setFormData(prev => ({
          ...prev,
          checklist: []
        }));
      }
    }
  }, [isReassigning, tasks]);

  // Debug form data changes
  useEffect(() => {
    if (isReassigning) {
      // Form data tracking removed for production
    }
  }, [formData, isReassigning]);

  // Update calendar position on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (calendarAnchorEl && calendarPosition) {
        const rect = calendarAnchorEl.getBoundingClientRect();
        setCalendarPosition({
          top: rect.bottom + 56,
          left: rect.left
        });
      }
    };

    const panelContent = document.querySelector('[data-panel-content]');
    if (panelContent) {
      panelContent.addEventListener('scroll', handleScroll);
      return () => panelContent.removeEventListener('scroll', handleScroll);
    }
  }, [calendarAnchorEl, calendarPosition]);

  // Handle click outside calendar to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (calendarAnchorEl) {
        // Check if the click is not on the calendar anchor element
        if (!calendarAnchorEl.contains(event.target as Node)) {
          // Check if the click is not on the calendar popover
          const calendarElement = document.querySelector('[role="presentation"]');
          if (!calendarElement || !calendarElement.contains(event.target as Node)) {
            handleCalendarClose();
          }
        }
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && calendarAnchorEl) {
        handleCalendarClose();
      }
    };

    if (calendarAnchorEl) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [calendarAnchorEl]);

  // Track previous thread ID to detect actual changes
  const prevThreadIdRef = useRef<string | undefined>(threadId);

  // Close panel when thread actually changes (not on initial open)
  useEffect(() => {
    if (open && threadId && prevThreadIdRef.current && prevThreadIdRef.current !== threadId) {
      onClose();
    }
    // Update the ref with current thread ID
    prevThreadIdRef.current = threadId;
  }, [threadId, open, onClose]);

  // Reset editing state when panel is closed or thread changes
  useEffect(() => {
    if (!open) {
      // Reset all editing states when panel is closed
      setIsEditingMode(false);
      setEditingChecklistIndex(null);
      setEditingChecklistValue('');
      setChecklistInputValue('');
      setShowChecklistInput(false);
    }
  }, [open]);

  // Reset editing state when thread changes
  useEffect(() => {
    if (threadId && prevThreadIdRef.current && prevThreadIdRef.current !== threadId) {
      // Reset all editing states when switching threads
      setIsEditingMode(false);
      setEditingChecklistIndex(null);
      setEditingChecklistValue('');
      setChecklistInputValue('');
      setShowChecklistInput(false);
    }
  }, [threadId]);

  const fetchMembers = async () => {
    if (!channelId) return;
    
    try {
      setMembersLoading(true);
      
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
      const response = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.CHANNELS.LIST_MEMBERS(channelId)}`, {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      // Handle the actual API response structure: { items: [...], total: number }
      const membersData = data.items || data.members || data || [];
      setMembers(Array.isArray(membersData) ? membersData : []);
      
    } catch (error) {
      console.error('Failed to fetch members:', error);
             // Fallback to mock data if API fails
       setMembers([
         { user_id: '1', name: 'Shay', email_id: 'shay@example.com', user_role: 'admin', membership_role: 'admin' },
         { user_id: '2', name: 'Ashok', email_id: 'ashok@example.com', user_role: 'user', membership_role: 'member' },
         { user_id: '3', name: 'John', email_id: 'john@example.com', user_role: 'user', membership_role: 'member' },
         { user_id: '4', name: 'Sarah', email_id: 'sarah@example.com', user_role: 'user', membership_role: 'member' },
       ]);
    } finally {
      setMembersLoading(false);
    }
  };

  const handleClose = () => {
    setFormData(initializeFormData());
    resetCalendarState();
    resetReassignCalendarState();
    setShowChecklistInput(false);
    setChecklistInputValue('');
    setIsReassigning(false);
    setIsChangingStatus(false);
    
    // Call the tasks API to refresh data for the chip when closing the panel
    // This covers: first-time task assignment, reassignment, and status changes
    if (threadId) {
      const refreshTasksData = async () => {
        try {
          const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
          const response = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.TASKS.LIST}?thread_id=${threadId}`, {
            method: 'GET',
            headers: {
              'accept': 'application/json',
              ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            }
          });
          
          if (response.ok) {
            // Trigger the callback to refresh tasks in the parent component
            if (onTaskAssigned) {
              onTaskAssigned();
            }
          }
        } catch (error) {
          console.error('Failed to refresh tasks data on close:', error);
        }
      };
      
      refreshTasksData();
    }
    
    onClose();
  };

  // Function to handle reassigning task
  const handleReassign = () => {
    setIsReassigning(true);
    setIsChangingStatus(false);
    
    // Reset calendar state when switching to reassign mode
    resetCalendarState();
    resetReassignCalendarState();
    
    // Populate form with existing task data
    if (tasks && tasks.length > 0) {
      const task = tasks[0];
      
      const newFormData = {
        taskName: task.task_details?.task_name || task.task_details?.title || task.title || '',
        comment: task.task_details?.description || '',
        assignTo: task.task_details?.assignee?.id || task.task_details?.assignee?.user_id || task.task_details?.assigned_to || task.assigned_to || '',
        priority: task.task_details?.priority || 'Medium',
        dueDate: task.task_details?.due_date ? formatDateForDisplay(task.task_details.due_date) : '',
        dueTime: task.task_details?.due_date ? formatTimeForDisplay(task.task_details.due_date) : '',
        checklist: task.checklists && Array.isArray(task.checklists) ? task.checklists.map((item: any) => item.text || item).filter(Boolean) : [],
        // Initialize other fields that might not be in the task data
        attachments: [],
        statusComment: ''
      };
      
      setFormData(newFormData);
      
      setFormData(newFormData);
    }
  };

  // Function to handle changing task status
  const handleChangeStatus = (event: React.MouseEvent<HTMLButtonElement>) => {
    setIsChangingStatus(true);
    setIsReassigning(false);
    
    // Reset calendar state when switching to status change mode
    resetCalendarState();
    
    // Store the selected status from the button's data-status attribute
    const selectedStatus = event.currentTarget.getAttribute('data-status') || '';
    setFormData(prev => ({ ...prev, selectedStatus }));
  };

  const handleInputChange = (fieldId: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [fieldId]: value
    }));
    
    // Clear validation error for this field when user starts typing
    if (validationErrors[fieldId]) {
      setValidationErrors(prev => ({
        ...prev,
        [fieldId]: ''
      }));
    }
  };

  // File upload functions
  const handleFileUpload = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const currentFiles = formData.attachments || [];
    const newFiles = Array.from(files);
    
    if (currentFiles.length + newFiles.length > 4) {
      setShowFileLimitWarning(true);
      setTimeout(() => setShowFileLimitWarning(false), 3000);
      return;
    }

    const updatedFiles = [...currentFiles, ...newFiles];
    handleInputChange('attachments', updatedFiles);
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (index: number) => {
    const currentFiles = formData.attachments || [];
    const updatedFiles = currentFiles.filter((_: any, i: number) => i !== index);
    handleInputChange('attachments', updatedFiles);
  };

  const getFileIcon = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return <PictureAsPdfIcon sx={{ fontSize: 16, color: '#d32f2f' }} />;
      case 'xlsx':
      case 'xls':
      case 'csv':
        return <TableChartIcon sx={{ fontSize: 16, color: '#2e7d32' }} />;
      default:
        return <InsertDriveFileIcon sx={{ fontSize: 16, color: '#1976d2' }} />;
    }
  };

  // Calendar functions
  const handleCalendarOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    
    // Position calendar below the field
    const rect = event.currentTarget.getBoundingClientRect();
    const top = rect.bottom + 10;
    const left = rect.left;
    
    setCalendarPosition({ top, left });
    setCalendarAnchorEl(event.currentTarget);
  };

  const handleCalendarClose = () => {
    // Close calendar immediately
    setCalendarAnchorEl(null);
    setCalendarPosition(null);
  };

  const resetCalendarState = () => {
    setCalendarAnchorEl(null);
    setCalendarPosition(null);
    setSelectedDate(null);
  };

  const handleReassignCalendarOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    
    // Position calendar below the field
    const rect = event.currentTarget.getBoundingClientRect();
    const top = rect.bottom + 10;
    const left = rect.left;
    
    setReassignCalendarPosition({ top, left });
    setReassignCalendarAnchorEl(event.currentTarget);
  };

  const handleReassignCalendarClose = () => {
    setReassignCalendarAnchorEl(null);
    setReassignCalendarPosition(null);
  };

  const handleReassignDateSelect = (date: Date) => {
    // Check if the date is in the past
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date < today) {
      return; // Don't allow selecting past dates
    }
    
    const formattedDate = date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
    
    setFormData(prev => ({
      ...prev,
      dueDate: formattedDate,
      dueTime: '' // Reset time when date changes
    }));
    setReassignSelectedDate(date);
    
    // Close calendar immediately after date selection
    handleReassignCalendarClose();
  };

  const resetReassignCalendarState = () => {
    setReassignCalendarAnchorEl(null);
    setReassignCalendarPosition(null);
    setReassignSelectedDate(null);
  };

  const handleDateSelect = (date: Date) => {
    // Check if the date is in the past
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date < today) {
      return; // Don't allow selecting past dates
    }
    
    const formattedDate = date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
    
    setFormData(prev => ({
      ...prev,
      dueDate: formattedDate,
      dueTime: '' // Reset time when date changes
    }));
    setSelectedDate(date);
    
    // Close calendar immediately after date selection
    handleCalendarClose();
  };



  const formatDateForDisplay = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
  };

  const renderCalendar = () => {
    const currentDate = selectedDate || new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();
    
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
    const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
    const startDate = new Date(firstDayOfMonth);
    startDate.setDate(startDate.getDate() - firstDayOfMonth.getDay());
    
    const days = [];
    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      days.push(date);
    }

    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];

         return (
       <Box sx={{ p: 1, minWidth: 240 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            {monthNames[currentMonth]} {currentYear}
          </Typography>
          <IconButton size="small" onClick={() => {
            const newDate = new Date(currentYear, currentMonth + 1, 1);
            setSelectedDate(newDate);
          }}>
            »
          </IconButton>
        </Box>
        
                 <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 1 }}>
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
            <Typography key={day} variant="caption" sx={{ textAlign: 'center', fontWeight: 'bold' }}>
              {day}
            </Typography>
          ))}
        </Box>
        
                 <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
           {days.map((date, index) => {
             const isCurrentMonth = date.getMonth() === currentMonth;
             const isSelected = selectedDate && 
               date.getDate() === selectedDate.getDate() &&
               date.getMonth() === selectedDate.getMonth() &&
               date.getFullYear() === selectedDate.getFullYear();
             const isToday = date.toDateString() === new Date().toDateString();
             
             // Check if date is in the past
             const today = new Date();
             today.setHours(0, 0, 0, 0);
             const isPastDate = date < today;
             
             return (
               <Box
                 key={index}
                 onClick={() => !isPastDate && handleDateSelect(date)}
                                    sx={{
                     width: 28,
                     height: 28,
                   display: 'flex',
                   alignItems: 'center',
                   justifyContent: 'center',
                   cursor: isPastDate ? 'not-allowed' : 'pointer',
                   borderRadius: 1,
                   backgroundColor: isSelected ? '#1976d2' : 'transparent',
                   color: isSelected ? 'white' : 
                     isPastDate ? 'text.disabled' : 
                     isCurrentMonth ? 'text.primary' : 'text.disabled',
                   fontWeight: isToday ? 'bold' : 'normal',
                   opacity: isPastDate ? 0.5 : 1,
                   '&:hover': {
                     backgroundColor: isPastDate ? 'transparent' : 
                       isSelected ? '#1976d2' : '#f5f5f5'
                   }
                 }}
               >
                 <Typography variant="caption">
                   {date.getDate()}
                 </Typography>
               </Box>
             );
           })}
         </Box>
      </Box>
    );
  };

  // Separate calendar renderer for reassignment mode
  const renderReassignCalendar = () => {
    const currentDate = reassignSelectedDate || new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();
    
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
    const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
    const startDate = new Date(firstDayOfMonth);
    startDate.setDate(startDate.getDate() - firstDayOfMonth.getDay());
    
    const days = [];
    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      days.push(date);
    }

    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];

         return (
       <Box sx={{ p: 1, minWidth: 240 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            {monthNames[currentMonth]} {currentYear}
          </Typography>
          <IconButton size="small" onClick={() => {
            const newDate = new Date(currentYear, currentMonth + 1, 1);
            setReassignSelectedDate(newDate);
          }}>
            »
          </IconButton>
        </Box>
        
                 <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 1 }}>
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
            <Typography key={day} variant="caption" sx={{ textAlign: 'center', fontWeight: 'bold' }}>
              {day}
            </Typography>
          ))}
        </Box>
        
                 <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
           {days.map((date, index) => {
             const isCurrentMonth = date.getMonth() === currentMonth;
             const isSelected = reassignSelectedDate && 
               date.getDate() === reassignSelectedDate.getDate() &&
               date.getMonth() === reassignSelectedDate.getMonth() &&
               date.getFullYear() === reassignSelectedDate.getFullYear();
             const isToday = date.toDateString() === new Date().toDateString();
             
             // Check if date is in the past
             const today = new Date();
             today.setHours(0, 0, 0, 0);
             const isPastDate = date < today;
             
             return (
               <Box
                 key={index}
                 onClick={() => !isPastDate && handleReassignDateSelect(date)}
                                    sx={{
                     width: 28,
                     height: 28,
                   display: 'flex',
                   alignItems: 'center',
                   justifyContent: 'center',
                   cursor: isPastDate ? 'not-allowed' : 'pointer',
                   borderRadius: 1,
                   backgroundColor: isSelected ? '#1976d2' : 'transparent',
                   color: isSelected ? 'white' : 
                     isPastDate ? 'text.disabled' : 
                     isCurrentMonth ? 'text.primary' : 'text.disabled',
                   fontWeight: isToday ? 'bold' : 'normal',
                   opacity: isPastDate ? 0.5 : 1,
                   '&:hover': {
                     backgroundColor: isPastDate ? 'transparent' : 
                       isSelected ? '#1976d2' : '#f5f5f5'
                   }
                 }}
               >
                 <Typography variant="caption">
                   {date.getDate()}
                 </Typography>
               </Box>
             );
           })}
         </Box>
      </Box>
    );
  };

  // Render dynamic form fields based on configuration
  const renderField = (field: FormField) => {
    const value = formData[field.id];
    const gridSize = field.gridSize || 12;
    
    // Debug logging for form field values
    if (isReassigning) {
      // Field rendering tracking removed for production
    }

    switch (field.type) {
             case 'text':
                   // Special handling for date field with calendar
          if (field.id === 'dueDate') {
            // Use different calendar handlers based on mode
            const calendarHandler = isReassigning ? handleReassignCalendarOpen : handleCalendarOpen;
            
            return (
              <Box key={field.id} data-gridsize={gridSize} sx={{ width: `${(gridSize / 12) * 100}%` }}>
                                 <TextField
                   fullWidth
                   placeholder="Due Date"
                   value={value || ''}
                  onChange={(e) => handleInputChange(field.id, e.target.value)}
                                     sx={{
                     '& .MuiOutlinedInput-root': {
                       borderRadius: 1,
                       height: '38px'
                     },
                     '& .MuiInputBase-input': {
                       textAlign: 'left',
                       cursor: 'pointer'
                     }
                   }}
                                     InputProps={{
                     startAdornment: (
                       <InputAdornment position="start">
                         <CalendarTodayIcon 
                           sx={{ fontSize: 16, color: 'text.secondary', cursor: 'pointer' }}
                           onClick={(e) => {
                             e.stopPropagation();
                             calendarHandler(e as any);
                           }}
                         />
                       </InputAdornment>
                     ),
                     readOnly: true,
                     onClick: (e) => {
                       e.stopPropagation();
                       calendarHandler(e as any);
                     },
                     onFocus: (e) => {
                       e.stopPropagation();
                       calendarHandler(e as any);
                     }
                   }}
                />
              </Box>
            );
          }
         
         return (
           <Box key={field.id} data-gridsize={gridSize} sx={{ 
             width: `${(gridSize / 12) * 100}%`,
             display: gridSize === 12 ? 'flex' : 'block',
             justifyContent: gridSize === 12 ? 'center' : 'flex-start'
           }}>
             <TextField
               fullWidth={field.fullWidth !== false}
               label={field.id === 'taskName' ? undefined : (field.required ? `${field.label} *` : field.label)}
               placeholder={field.placeholder}
               value={value}
               onChange={(e) => handleInputChange(field.id, e.target.value)}
               multiline={field.multiline}
               rows={field.rows}
               error={!!validationErrors[field.id]}
               helperText={validationErrors[field.id]}
               sx={{
                 width: gridSize === 12 ? '100%' : 'auto',
                 '& .MuiOutlinedInput-root': {
                   borderRadius: 1,
                   height: '38px'
                 },
                 '& .MuiInputBase-input': {
                   textAlign: 'left'
                 }
               }}
             />
           </Box>
         );

                                                       case 'select':
           let options = field.options || [];
           if (field.apiDependent && field.apiField === 'members') {
             // Ensure members is always an array
             const membersArray = Array.isArray(members) ? members : [];
             options = membersArray.map(member => ({ value: member.user_id, label: member.name }));
           } else if (field.id === 'dueTime') {
             // Generate time options for time field
             options = generateTimeOptions();
           }
          
                    return (
             <Box key={field.id} data-gridsize={gridSize} sx={{ width: `${(gridSize / 12) * 100}%` }}>
               {field.id === 'dueTime' ? (
                 // Special handling for time field with clock icon
                 <Box sx={{ position: 'relative' }}>
                   <select
                     value={value}
                     onChange={(e) => handleInputChange(field.id, e.target.value)}
                     disabled={field.apiDependent && membersLoading}
                                           style={{
                        width: '100%',
                        padding: '8px 12px 8px 40px', // Extra left padding for icon
                        fontSize: '14px',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        backgroundColor: theme.palette.background.paper,
                        color: theme.palette.text.primary,
                        cursor: 'pointer',
                        height: '38px' // Increased height to match other fields
                      }}
                   >
                     {field.apiDependent && membersLoading ? (
                       <option disabled>Loading members...</option>
                     ) : (
                                               <>
                          <option value="" disabled style={{ color: '#757575' }}>Time</option>
                          {options.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </>
                     )}
                   </select>
                   <AccessTimeIcon
                     sx={{
                       position: 'absolute',
                       left: '12px',
                       top: '50%',
                       transform: 'translateY(-50%)',
                       fontSize: 16,
                       color: 'text.secondary',
                       pointerEvents: 'none'
                     }}
                   />
                 </Box>
               ) : (
                 // Regular select for other fields
                 <Box sx={{ width: '100%' }}>
                   <select
                     value={value}
                     onChange={(e) => handleInputChange(field.id, e.target.value)}
                     disabled={field.apiDependent && membersLoading}
                     style={{
                       width: '100%',
                       padding: '8px 12px',
                       fontSize: '14px',
                       border: validationErrors[field.id] ? '1px solid #d32f2f' : '1px solid #d1d5db',
                       borderRadius: '4px',
                       backgroundColor: theme.palette.background.paper,
                       color: theme.palette.text.primary,
                       cursor: 'pointer',
                       height: '38px'
                     }}
                   >
                     {field.apiDependent && membersLoading ? (
                       <option disabled>Loading members...</option>
                     ) : (
                       <>
                         <option value="" disabled>
                           {field.id === 'assignTo' ? 'Assign to *' : 
                            `Select ${field.label}${field.required ? ' *' : ''}`}
                         </option>
                         {options.map((option) => (
                           <option key={option.value} value={option.value}>
                             {option.label}
                           </option>
                         ))}
                       </>
                     )}
                   </select>
                   {validationErrors[field.id] && (
                     <Typography 
                       variant="caption" 
                       color="error" 
                       sx={{ 
                         display: 'block', 
                         mt: 0.5, 
                         ml: 1.5,
                         fontSize: '0.75rem'
                       }}
                     >
                       {validationErrors[field.id]}
                     </Typography>
                   )}
                 </Box>
               )}
             </Box>
           );

                                                       case 'textarea':
          return (
            <Box key={field.id} data-gridsize={gridSize} sx={{ width: `${(gridSize / 12) * 100}%`, marginTop: '10px' }}>
              <TextField
                fullWidth={field.fullWidth !== false}
                multiline
                rows={field.rows || 2}
                label={field.required ? `${field.label} *` : field.label}
                placeholder={field.placeholder}
                value={value}
                onChange={(e) => handleInputChange(field.id, e.target.value)}
                error={!!validationErrors[field.id]}
                helperText={validationErrors[field.id]}
                                 sx={{
                   '& .MuiOutlinedInput-root': {
                     borderRadius: 1,
                     height: '100px',
                     maxHeight: '100px',
                     resize: 'none',
                     alignItems: 'flex-start',
                     padding: '8px 12px',
                     overflow: 'hidden',
                     cursor: 'text'
                   },
                   '& .MuiInputBase-input': {
                     textAlign: 'left',
                     height: '84px',
                     maxHeight: '84px',
                     padding: '0',
                     overflow: 'auto',
                     resize: 'none',
                     cursor: 'text',
                     '&::-webkit-scrollbar': {
                       width: '8px'
                     },
                     '&::-webkit-scrollbar-track': {
                       background: '#f1f1f1',
                       borderRadius: '4px'
                     },
                     '&::-webkit-scrollbar-thumb': {
                       background: '#c1c1c1',
                       borderRadius: '4px',
                       cursor: 'pointer',
                       '&:hover': {
                         background: '#a8a8a8'
                       }
                     }
                   }
                 }}
              />
            </Box>
          );

                                                                                                                                                                                                                                                                                   case 'dynamic-list':
           const listItems = Array.isArray(value) ? value : [];
          
          // Drag and drop handlers
          const handleDragStart = (e: React.DragEvent, index: number) => {
            setDraggedItemIndex(index);
            e.dataTransfer.effectAllowed = 'move';
          };

          const handleDragOver = (e: React.DragEvent) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
          };

          const handleDrop = (e: React.DragEvent, dropIndex: number) => {
            e.preventDefault();
            if (draggedItemIndex === null || draggedItemIndex === dropIndex) return;
            
            if (isReassigning && tasks && tasks.length > 0 && tasks[0].checklists) {
              // Reorder API checklist items
              const newChecklists = [...tasks[0].checklists];
              const [draggedItem] = newChecklists.splice(draggedItemIndex, 1);
              newChecklists.splice(dropIndex, 0, draggedItem);
              
              // Call the reorder API
              handleReorderChecklists(newChecklists);
            } else {
              // Reorder local form items
            const newList = [...listItems];
            const [draggedItem] = newList.splice(draggedItemIndex, 1);
            newList.splice(dropIndex, 0, draggedItem);
            
            handleInputChange(field.id, newList);
            }
            setDraggedItemIndex(null);
          };

          const handleEditChecklist = (index: number, currentValue: string) => {
            setEditingChecklistIndex(index);
            setEditingChecklistValue(currentValue);
            setChecklistInputValue(currentValue); // Set the main input field value
            setIsEditingMode(true);
            setShowChecklistInput(true);
          };

          const handleSaveEdit = () => {
            if (editingChecklistIndex !== null && checklistInputValue.trim()) {
              const newList = [...listItems];
              newList[editingChecklistIndex] = checklistInputValue.trim();
              handleInputChange(field.id, newList);
              setEditingChecklistIndex(null);
              setEditingChecklistValue('');
              setChecklistInputValue(''); // Clear the input field
              setIsEditingMode(false);
              setShowChecklistInput(true); // Keep the input field open for adding new items
            }
          };

          const handleCancelEdit = () => {
            setEditingChecklistIndex(null);
            setEditingChecklistValue('');
            setIsEditingMode(false);
            setShowChecklistInput(false);
          };

          const handleDeleteChecklist = (index: number) => {
            const newList = listItems.filter((_, i) => i !== index);
            handleInputChange(field.id, newList);
          };

                     return (
             <Box key={field.id} data-gridsize={gridSize} sx={{ width: `${(gridSize / 12) * 100}%` }}>
               <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                 {field.label}
               </Typography>
              
               {/* Display existing checklist items */}
               {(isReassigning && tasks && tasks.length > 0 && tasks[0].checklists ? 
                 tasks[0].checklists.map((item: any, index: number) => (
                   <Box 
                     key={item.id || index} 
                     sx={{ 
                       display: 'flex', 
                       gap: 1, 
                       mb: 1, 
                       alignItems: 'center',
                       opacity: draggedItemIndex === index ? 0.5 : 1,
                       transform: draggedItemIndex === index ? 'scale(0.95)' : 'scale(1)',
                       transition: 'all 0.2s ease'
                     }}
                     draggable
                     onDragStart={(e) => handleDragStart(e, index)}
                     onDragOver={handleDragOver}
                     onDrop={(e) => handleDrop(e, index)}
                   >
                     {/* Item content */}
                     <Box sx={{ 
                       flex: 1, 
                       py: 0.25, // Reduced vertical padding for height
                       px: 1,    // Keep horizontal padding
                       border: `1px solid ${theme.palette.divider}`, 
                       borderRadius: 1, 
                       backgroundColor: editingChecklistIndex === item.id
                         ? (isDark ? theme.palette.grey[700] : '#fff3cd')
                         : (isDark ? theme.palette.grey[800] : '#f9f9f9'),
                       display: 'flex',
                       alignItems: 'center',
                       gap: 1
                     }}>
                       <DragIndicatorIcon sx={{ color: 'text.secondary', cursor: 'grab', fontSize: 16 }} />
                       <Checkbox
                         checked={item.is_completed || false}
                         onChange={() => {
                           if (!item.id) {
                             console.error('Checklist item missing ID:', item);
                             toast.showError('Cannot toggle: Checklist item missing ID');
                             return;
                           }
                           handleChecklistToggle(item);
                         }}
                         size="small"
                         sx={{
                           color: '#1976d2',
                           '&.Mui-checked': {
                             color: '#1976d2',
                           },
                         }}
                       />
                       <Typography 
                         title={item.text} // Show full text on hover
                         sx={{ 
                           flex: 1,
                           overflow: 'hidden',
                           textOverflow: 'ellipsis',
                           whiteSpace: 'nowrap',
                           minWidth: 0, // This is important for flex items with ellipsis
                           maxWidth: '200px', // Back to original width
                           textDecoration: item.is_completed ? 'line-through' : 'none',
                           color: item.is_completed ? 'text.secondary' : 'text.primary'
                         }}
                       >
                         {item.text}
                       </Typography>
                       <IconButton
                         onClick={() => {
                           if (item.is_completed) return; // Don't allow editing completed items
                           if (!item.id) {
                             console.error('Checklist item missing ID:', item);
                             toast.showError('Cannot edit: Checklist item missing ID');
                             return;
                           }
                           handleEditChecklistItem(item);
                         }}
                         size="small"
                         disabled={item.is_completed}
                         sx={{ 
                           color: item.is_completed ? 'text.disabled' : 'primary.main',
                           '&:disabled': {
                             color: 'text.disabled'
                           }
                         }}
                       >
                         <EditIcon sx={{ fontSize: 16 }} />
                       </IconButton>
                       <IconButton
                         onClick={() => {
                           if (item.is_completed) return; // Don't allow deleting completed items
                           if (!item.id) {
                             console.error('Checklist item missing ID:', item);
                             toast.showError('Cannot delete: Checklist item missing ID');
                             return;
                           }
                           handleDeleteChecklistItem(item);
                         }}
                         size="small"
                         disabled={item.is_completed}
                         sx={{ 
                           color: item.is_completed ? 'text.disabled' : 'error.main',
                           '&:disabled': {
                             color: 'text.disabled'
                           }
                         }}
                       >
                         <DeleteIcon sx={{ fontSize: 16 }} />
                       </IconButton>
                     </Box>
                   </Box>
                 )) :
                 listItems.filter(item => item.trim()).map((item, index: number) => (
                 <Box 
                   key={index} 
                   sx={{ 
                     display: 'flex', 
                     gap: 1, 
                     mb: 1, 
                     alignItems: 'center',
                     opacity: draggedItemIndex === index ? 0.5 : 1,
                     transform: draggedItemIndex === index ? 'scale(0.95)' : 'scale(1)',
                     transition: 'all 0.2s ease'
                   }}
                   draggable
                   onDragStart={(e) => handleDragStart(e, index)}
                   onDragOver={handleDragOver}
                   onDrop={(e) => handleDrop(e, index)}
                 >
                   {/* Item content */}
                   <Box sx={{ 
                     flex: 1, 
                     p: 1, 
                     border: `1px solid ${theme.palette.divider}`, 
                     borderRadius: 1, 
                     backgroundColor: editingChecklistIndex === index
                       ? (isDark ? theme.palette.grey[700] : '#fff3cd')
                       : (isDark ? theme.palette.grey[800] : '#f9f9f9'),
                     display: 'flex',
                     alignItems: 'center',
                     gap: 1
                   }}>
                     <DragIndicatorIcon sx={{ color: 'text.secondary', cursor: 'grab', fontSize: 16 }} />
                     <Typography 
                       title={item} // Show full text on hover
                       sx={{ 
                         flex: 1,
                         overflow: 'hidden',
                         textOverflow: 'ellipsis',
                         whiteSpace: 'nowrap',
                         minWidth: 0, // This is important for flex items with ellipsis
                         maxWidth: '200px' // Limit width to 200px
                       }}
                     >
                       {item}
                     </Typography>
                     <IconButton
                       onClick={() => handleEditChecklist(index, item)}
                       size="small"
                       sx={{ color: 'primary.main' }}
                       disabled={isEditingMode}
                     >
                       <EditIcon sx={{ fontSize: 16 }} />
                     </IconButton>
                     <IconButton
                       onClick={() => handleDeleteChecklist(index)}
                       size="small"
                       sx={{ color: 'error.main' }}
                       disabled={isEditingMode}
                     >
                       <DeleteIcon sx={{ fontSize: 16 }} />
                     </IconButton>
                   </Box>
                 </Box>
                 ))
               )}
                 
               {/* Show input field when adding new item or editing - for reassign form */}
               {isReassigning && tasks && tasks.length > 0 && tasks[0].checklists && showChecklistInput && (
                 <Box sx={{ mb: 2, mt: 2 }}>
                   <TextField
                     fullWidth
                     placeholder={isEditingMode ? "Edit checklist item" : "Enter checklist"}
                     value={checklistInputValue}
                     onChange={(e) => setChecklistInputValue(e.target.value)}
                     onKeyPress={(e) => {
                      if (e.key === 'Enter' && checklistInputValue.trim()) {
                        if (isEditingMode) {
                          // Call API to save edited checklist item
                          handleSaveEditChecklist();
                        } else {
                          // Call API to add checklist item when reassigning
                          handleAddChecklistItemAPI(checklistInputValue.trim(), tasks[0].checklists.length);
                        }
                      } else if (e.key === 'Escape') {
                        handleCancelEditChecklist();
                      }
                    }}
                     autoFocus={isEditingMode}
                     sx={{
                       '& .MuiOutlinedInput-root': {
                         borderRadius: 1,
                         height: '38px'
                       },
                       '& .MuiInputBase-input': {
                         textAlign: 'left'
                       }
                     }}
                   />
                   <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1 }}>
                     <Button
                       onClick={isEditingMode ? handleCancelEditChecklist : () => {
                         setShowChecklistInput(false);
                         setChecklistInputValue('');
                       }}
                       sx={{
                         color: 'text.secondary',
                         '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                       }}
                     >
                       Cancel
                     </Button>
                     <Button
                      onClick={() => {
                        if (checklistInputValue.trim()) {
                          if (isEditingMode) {
                            // Call API to save edited checklist item
                            handleSaveEditChecklist();
                          } else {
                            // Call API to add checklist item when reassigning
                            handleAddChecklistItemAPI(checklistInputValue.trim(), tasks[0].checklists.length);
                          }
                        }
                      }}
                      variant="contained"
                      disabled={!checklistInputValue.trim()}
                      sx={{
                        backgroundColor: '#1976d2',
                        '&:hover': { backgroundColor: '#1565c0' }
                      }}
                    >
                      {isEditingMode ? 'Update' : 'Add'}
                    </Button>
                   </Box>
                 </Box>
               )}
               
               {/* Show add button when not adding - for reassign form */}
               {isReassigning && tasks && tasks.length > 0 && tasks[0].checklists && !showChecklistInput && (
                 <Button
                   startIcon={<AddIcon />}
                   onClick={() => setShowChecklistInput(true)}
                   fullWidth
                   sx={{
                     color: 'primary.main', // Theme primary so dark/light stay consistent
                     textTransform: 'none',
                     border: '1px solid',
                     borderColor: 'primary.main',
                     borderRadius: 1,
                     p: 1,
                     mt: 2,
                     justifyContent: 'center',
                     // Match panel surface in dark mode; keep light gray tile in light mode
                     backgroundColor: isDark ? theme.palette.grey[800] : '#f8f9fa',
                     '&:hover': {
                       backgroundColor: isDark ? theme.palette.action.hover : 'rgba(25, 118, 210, 0.04)',
                       borderColor: 'primary.main',
                     },
                     '&:disabled': {
                       color: 'action.disabled',
                       borderColor: 'divider',
                       backgroundColor: isDark ? theme.palette.grey[900] : '#f5f5f5',
                     },
                   }}
                 >
                   Checklist item
                 </Button>
               )}
                 
               {/* Show input field when adding new item or editing - only for non-reassign mode */}
               {showChecklistInput && !(isReassigning && tasks && tasks.length > 0 && tasks[0].checklists) && (
                 <Box sx={{ mb: 2 }}>
                   <TextField
                     key={isEditingMode ? `edit-${editingChecklistIndex}` : 'new-checklist'}
                     fullWidth
                     placeholder={isEditingMode ? "Edit checklist item" : "Enter checklist"}
                     value={checklistInputValue}
                     onChange={(e) => {
                       setChecklistInputValue(e.target.value);
                     }}
                     onBlur={(e) => {
                       setChecklistInputValue(e.target.value);
                     }}
                     onKeyPress={(e) => {
                      if (e.key === 'Enter' && checklistInputValue.trim()) {
                        if (isEditingMode) {
                          // Always call API to save edited checklist item
                            handleSaveEditChecklist();
                        } else {
                          if (isReassigning && tasks && tasks.length > 0) {
                            // Call API to add checklist item when reassigning
                            handleAddChecklistItemAPI(checklistInputValue.trim(), listItems.length);
                          } else {
                            // Normal behavior for first-time assignment
                            const newList = [...listItems, checklistInputValue.trim()];
                            handleInputChange(field.id, newList);
                          }
                          setChecklistInputValue('');
                          // Keep the input field open for next item
                        }
                      } else if (e.key === 'Escape') {
                        if (isReassigning && tasks && tasks.length > 0) {
                          handleCancelEditChecklist();
                        } else {
                        handleCancelEdit();
                        }
                      }
                    }}
                     autoFocus={isEditingMode}
                     sx={{
                       '& .MuiOutlinedInput-root': {
                         borderRadius: 1,
                         height: '38px'
                       },
                       '& .MuiInputBase-input': {
                         textAlign: 'left'
                       }
                     }}
                   />
                   <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1 }}>
                     <Button
                       onClick={isEditingMode ? (isReassigning && tasks && tasks.length > 0 ? handleCancelEditChecklist : handleCancelEdit) : () => {
                         setShowChecklistInput(false);
                         setChecklistInputValue('');
                       }}
                       sx={{
                         color: 'text.secondary',
                         '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                       }}
                     >
                       {isEditingMode ? 'Cancel' : 'Cancel'}
                     </Button>
                     <Button
                      onClick={() => {
                        if (checklistInputValue.trim()) {
                          if (isEditingMode) {
                            if (isReassigning && tasks && tasks.length > 0) {
                              // Call API to save edited checklist item
                              handleSaveEditChecklist();
                            } else {
                              // Save local form item
                            handleSaveEdit();
                            }
                          } else {
                            if (isReassigning && tasks && tasks.length > 0) {
                              // Call API to add checklist item when reassigning
                              handleAddChecklistItemAPI(checklistInputValue.trim(), listItems.length);
                            } else {
                              // Normal behavior for first-time assignment
                              const newList = [...listItems, checklistInputValue.trim()];
                              handleInputChange(field.id, newList);
                            }
                            setChecklistInputValue('');
                            // Keep the input field open for next item
                          }
                        }
                      }}
                     >
                       {isEditingMode ? 'Update' : 'Add'}
                     </Button>
                   </Box>
                 </Box>
               )}
                 
               {/* Show add button when not adding - only for non-reassign mode */}
               {!showChecklistInput && !(isReassigning && tasks && tasks.length > 0 && tasks[0].checklists) && (
                 <Button
                   startIcon={<AddIcon />}
                   onClick={() => setShowChecklistInput(true)}
                   fullWidth
                   sx={{
                     color: 'primary.main', // Theme primary so dark/light stay consistent
                     textTransform: 'none',
                     border: '1px solid',
                     borderColor: 'primary.main',
                     borderRadius: 1,
                     p: 1,
                     justifyContent: 'center',
                     // Match panel surface in dark mode; keep light gray tile in light mode
                     backgroundColor: isDark ? theme.palette.grey[800] : '#f8f9fa',
                     '&:hover': {
                       backgroundColor: isDark ? theme.palette.action.hover : 'rgba(25, 118, 210, 0.04)',
                       borderColor: 'primary.main',
                     },
                     '&:disabled': {
                       color: 'action.disabled',
                       borderColor: 'divider',
                       backgroundColor: isDark ? theme.palette.grey[900] : '#f5f5f5',
                     },
                   }}
                 >
                   Checklist item
                 </Button>
               )}
             </Box>
           );

                                                                                                               case 'file-upload':
          // Hide attachment icon when assigning or reassigning tasks
          if (isReassigning || !isReassigning) {
            return null;
          }
          
          const files = Array.isArray(value) ? value : [];
          return (
            <Box key={field.id} data-gridsize={gridSize} sx={{ width: `${(gridSize / 12) * 100}%` }}>
              {/* Display uploaded files */}
              {files.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                  {files.map((file: File, index: number) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        p: 0.5,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 1,
                        backgroundColor: isDark ? theme.palette.grey[800] : '#f9f9f9',
                        maxWidth: '100%'
                      }}
                    >
                      {getFileIcon(file.name)}
                      <Typography
                        variant="caption"
                        sx={{
                          maxWidth: '120px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: '12px'
                        }}
                      >
                        {file.name}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveFile(index)}
                        sx={{
                          p: 0.25,
                          color: '#1976d2',
                          '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.04)' }
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton
                  onClick={handleFileUpload}
                  sx={{
                    width: '32px',
                    height: '32px',
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: '50%',
                    backgroundColor: isDark ? theme.palette.grey[800] : '#f8f9fa',
                    color: 'text.secondary',
                    position: 'relative',
                    '&:hover': { 
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      border: '1px solid #ccc'
                    }
                  }}
                >
                  <AttachFileIcon sx={{ fontSize: 16 }} />
                  {files.length > 0 && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -4,
                        right: -4,
                        backgroundColor: '#1976d2',
                        color: 'white',
                        borderRadius: '50%',
                        width: '16px',
                        height: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '10px',
                        fontWeight: 'bold'
                      }}
                    >
                      {files.length}
                    </Box>
                  )}
                </IconButton>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png"
                />
              </Box>
              
              {/* File limit warning */}
              {showFileLimitWarning && (
                <Typography
                  variant="caption"
                  sx={{
                    color: '#d32f2f',
                    display: 'block',
                    mt: 1,
                    fontSize: '12px'
                  }}
                >
                  Maximum 4 files allowed. Please remove some files before adding more.
                </Typography>
              )}
            </Box>
          );

      default:
        return null;
    }
  };

  // Validation function
  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    // Validate task name
    if (!formData.taskName?.trim()) {
      errors.taskName = 'Task name is required';
    }
    
    // Validate assignee
    if (!formData.assignTo) {
      errors.assignTo = 'Please select an assignee';
    }
    
    // Validate message ID
    if (!messageId) {
      errors.general = 'Message ID is required. Please select a message first.';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      setValidationErrors({}); // Clear previous validation errors
      
      // Validate required fields
      if (!validateForm()) {
        console.error('Missing required fields');
        toast.showError('Please fill in all required fields');
        return;
      }

      // Find the assigned user details
      const assignedUser = members.find(member => member.user_id === formData.assignTo);
      if (!assignedUser) {
        console.error('Assigned user not found');
        return;
      }

      // Create payload with only fields that have values
      const payload: any = {
        title: formData.taskName.trim(),
        message_id: messageId,
        assigned_to: formData.assignTo,
        status: "Open",
        source: "manual",
        priority: formData.priority || 'Medium'
      };

      // Only add description if it has a value
      if (formData.comment && formData.comment.trim()) {
        payload.description = formData.comment.trim();
      }

      // Only add due_date if both date and time are provided
      if (formData.dueDate && formData.dueTime) {
        payload.due_date = formatDateTimeForAPI(formData.dueDate, formData.dueTime);
      }

      // Only add checklists if they have values
      if (formData.checklist && formData.checklist.length > 0) {
        const validChecklists = formData.checklist
          .filter((item: string) => item.trim())
          .map((item: string, index: number) => ({
            text: item.trim(),
            order_index: index
          }));
        
        if (validChecklists.length > 0) {
          payload.checklists = validChecklists;
        }
      }

      
      // Make API call
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
      const response = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.TASKS.CREATE}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'accept': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify(payload)
      });
      
      const result = await response.json();
    
      // Task assigned successfully - no need to set assigned task details
      // The panel will automatically show task management interface after refresh
      
      // Don't set isAssigned to true - we want to show task management interface
      // The panel will automatically show task management after tasks are refreshed
      
      if (onTaskAssigned) {
        onTaskAssigned();
      }

    } catch (error) {
      console.error('Failed to assign task:', error);
      
      // Show error message
      const errorMsg = `Failed to assign task: ${error instanceof Error ? error.message : 'Unknown error'}`;
      setErrorMessage(errorMsg);
      
      // Auto-dismiss error message after 7 seconds
      setTimeout(() => {
        setErrorMessage(null);
      }, 7000);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Function to handle reassigning task to a new user
  const handleReassignSubmit = async () => {
    if (!formData.taskName?.trim() || !formData.assignTo || !tasks || tasks.length === 0) return;
    
    try {
      setIsSubmitting(true);
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
      
      // Update existing task with the reassigned data using PUT API
      const response = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.TASKS.UPDATE(tasks[0].id)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'accept': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          title: formData.taskName,
                     description: formData.comment || tasks[0].task_details?.description || '',
          assigned_to: formData.assignTo,
          status: (tasks[0].task_details?.status === 'Closed' || tasks[0].task_details?.status === 'Close' || tasks[0].task_details?.status === 'close') ? 'Open' : (tasks[0].task_details?.status || 'Open'),
          priority: (formData.priority ? formData.priority.charAt(0).toUpperCase() + formData.priority.slice(1).toLowerCase() : 'Medium'),
          due_date: (formData.dueDate && formData.dueTime) ? formatDateTimeForAPI(formData.dueDate, formData.dueTime) : 
                    (formData.dueDate ? formatDateForAPI(formData.dueDate) : (tasks[0].task_details?.due_date || null)),
          tags: tasks[0].task_details?.tags || [],
          task_metadata: tasks[0].task_details?.task_metadata || {},
          // Use the same message ID from first-time assignment
          message_id: messageId,
          source: tasks[0].task_details?.source || 'manual',
          checklists: formData.checklist || tasks[0].checklists || []
        })
      });
      
      if (response.ok) {
        toast.showSuccess('Task reassigned successfully');
        
        // Call the tasks API to get the latest data
        try {
          const tasksResponse = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.TASKS.LIST}?thread_id=${threadId}`, {
            method: 'GET',
            headers: {
              'accept': 'application/json',
              ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            }
          });
          
          if (tasksResponse.ok) {
            // Update the tasks data if needed
            if (onTaskAssigned) {
              onTaskAssigned(); // This will refresh the tasks
            }
          }
        } catch (error) {
          console.error('Failed to fetch updated tasks:', error);
        }
        
        setIsReassigning(false);
        setFormData(initializeFormData());
      } else {
        throw new Error('Failed to reassign task');
      }
    } catch (error) {
      console.error('Failed to reassign task:', error);
      toast.showError('Failed to reassign task');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Function to handle adding checklist item via API when reassigning
  const handleAddChecklistItemAPI = async (text: string, orderIndex: number) => {
    if (!tasks || tasks.length === 0) return;
    
    try {
      // Show loading toast
      toast.showInfo('Adding checklist item...');
      
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
      
      // Add new checklist item to the task
       const response = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.CHECKLISTS.ADD_ITEM(tasks[0].id)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'accept': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          text: text.trim(),
          order_index: orderIndex,
        })
      });
      
      if (response.ok) {
        // Show success toast
        toast.showSuccess('Checklist item added successfully');
        
        // Clear the input field after successful addition
        setChecklistInputValue('');
        
        // Call the tasks API to get the latest data
        try {
          const tasksResponse = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.TASKS.LIST}?thread_id=${threadId}`, {
            method: 'GET',
            headers: {
              'accept': 'application/json',
              ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            }
          });
          
          if (tasksResponse.ok) {
            // Update the tasks data if needed
            if (onTaskAssigned) {
              onTaskAssigned(); // This will refresh the tasks
            }
          }
        } catch (error) {
          console.error('Failed to fetch updated tasks:', error);
          toast.showWarning('Checklist item added but failed to refresh data');
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: Failed to add checklist item`);
      }
    } catch (error) {
      console.error('Failed to add checklist item:', error);
      toast.showError(`Failed to add checklist item: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // Function to handle checklist item toggle (complete/incomplete)
  const handleChecklistToggle = async (item: any) => {
    if (!tasks || tasks.length === 0) return;
    
    try {
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
      
      const response = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.CHECKLISTS.COMPLETE_ITEM(item.id)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'accept': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          is_completed: !item.is_completed, // true if checking, false if unchecking
          comments: ''
        })
      });
     
     if (response.ok) {
       toast.showSuccess(`Checklist item ${item.is_completed ? 'marked as incomplete' : 'marked as complete'}`);
       
       // Call the tasks API to get the latest data
       try {
         const tasksResponse = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.TASKS.LIST}?thread_id=${threadId}`, {
           method: 'GET',
           headers: {
             'accept': 'application/json',
             ...(token ? { 'Authorization': `Bearer ${token}` } : {})
           }
         });
         
         if (tasksResponse.ok) {
           // Update the tasks data if needed
           if (onTaskAssigned) {
             onTaskAssigned(); // This will refresh the tasks
           }
         }
       } catch (error) {
         console.error('Failed to fetch updated tasks:', error);
       }
     } else {
       throw new Error('Failed to toggle checklist item');
     }
   } catch (error) {
     console.error('Failed to toggle checklist item:', error);
     toast.showError('Failed to toggle checklist item');
   }
 };

   // Function to handle editing checklist item
  const handleEditChecklistItem = (item: any) => {
    setEditingChecklistIndex(item.id);
    setEditingChecklistValue(item.text);
    setChecklistInputValue(item.text); // Set the input field value
    setIsEditingMode(true); // Set editing mode
    setShowChecklistInput(true);
  };

  // Function to handle updating checklist item via API
  const handleUpdateChecklistItemAPI = async (itemId: string, newText: string) => {
    if (!tasks || tasks.length === 0) return;
    
    try {
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
      
      
      const requestBody = {
        text: newText.trim()
      };
      
      
      const response = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.CHECKLISTS.UPDATE_ITEM(itemId)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'accept': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify(requestBody)
      });
      
      if (response.ok) {
        
        // Call the tasks API to get the latest data
        try {
          const tasksResponse = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.TASKS.LIST}?thread_id=${threadId}`, {
            method: 'GET',
            headers: {
              'accept': 'application/json',
              ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            }
          });
          
          if (tasksResponse.ok) {
            // Update the tasks data if needed
            if (onTaskAssigned) {
              onTaskAssigned(); // This will refresh the tasks
            }
          }
        } catch (error) {
          console.error('Failed to fetch updated tasks:', error);
          toast.showWarning('Checklist updated but failed to refresh data');
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: Failed to update checklist item`);
      }
    } catch (error) {
      console.error('Failed to update checklist item:', error);
      toast.showError(`Failed to update checklist item: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
 };

  // Function to handle saving edited checklist item
  const handleSaveEditChecklist = async () => {
    if (!editingChecklistIndex || !checklistInputValue.trim() || !tasks || tasks.length === 0) return;
    
    // Use the current input value, not the stored editing value
    const currentText = checklistInputValue.trim();
    
    // Call the API to update the checklist item with the updated text from input field
    await handleUpdateChecklistItemAPI(editingChecklistIndex.toString(), currentText);
    
    // Reset editing state
    setEditingChecklistIndex(null);
    setEditingChecklistValue('');
    setChecklistInputValue('');
    setIsEditingMode(false);
    setShowChecklistInput(false);
  };

  // Function to handle canceling edit
  const handleCancelEditChecklist = () => {
    setEditingChecklistIndex(null);
    setEditingChecklistValue('');
    setShowChecklistInput(false);
  };

  // Function to handle deleting checklist item
  const handleDeleteChecklistItem = async (item: any) => {
    if (!tasks || tasks.length === 0) return;
    
    try {
      // Show loading toast
      toast.showInfo('Deleting checklist item...');
      
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
      
      const response = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.CHECKLISTS.DELETE_ITEM(item.id)}`, {
        method: 'DELETE',
        headers: {
          'accept': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      
     if (response.ok) {
       // Show success toast
       toast.showSuccess('Checklist item deleted successfully');
       
       // Call the tasks API to get the latest data
       try {
         const tasksResponse = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.TASKS.LIST}?thread_id=${threadId}`, {
           method: 'GET',
           headers: {
             'accept': 'application/json',
             ...(token ? { 'Authorization': `Bearer ${token}` } : {})
           }
         });
         
         if (tasksResponse.ok) {
           // Update the tasks data if needed
           if (onTaskAssigned) {
             onTaskAssigned(); // This will refresh the tasks
           }
         }
       } catch (error) {
         console.error('Failed to fetch updated tasks:', error);
         toast.showWarning('Checklist deleted but failed to refresh data');
       }
     } else {
       const errorData = await response.json().catch(() => ({}));
       throw new Error(errorData.message || `HTTP ${response.status}: Failed to delete checklist item`);
     }
   } catch (error) {
     console.error('Failed to delete checklist item:', error);
     toast.showError(`Failed to delete checklist item: ${error instanceof Error ? error.message : 'Unknown error'}`);
   }
 };

  // Function to handle reordering checklist items
  const handleReorderChecklists = async (reorderedItems: any[]) => {
    if (!tasks || tasks.length === 0) return;
    
    try {
      // Show loading toast
      toast.showInfo('Reordering checklist items...');
      
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
      
      // Prepare reorder data
      const reorderData = reorderedItems.map((item, index) => ({
        checklist_id: item.id,
        new_order: index
      }));
      
      const response = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.CHECKLISTS.REORDER_ITEMS(tasks[0].id)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'accept': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          // task_id: tasks[0].id,
          checklist_orders: reorderData
        })
      });
      
      if (response.ok) {
        // Show success toast
        toast.showSuccess('Checklist items reordered successfully');
        
        // Call the tasks API to get the latest data
        try {
          const tasksResponse = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.TASKS.LIST}?thread_id=${threadId}`, {
            method: 'GET',
            headers: {
              'accept': 'application/json',
              ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            }
          });
          
          if (tasksResponse.ok) {
            // Update the tasks data if needed
            if (onTaskAssigned) {
              onTaskAssigned(); // This will refresh the tasks
            }
          }
        } catch (error) {
          console.error('Failed to fetch updated tasks:', error);
          toast.showWarning('Checklist reordered but failed to refresh data');
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: Failed to reorder checklist items`);
      }
    } catch (error) {
      console.error('Failed to reorder checklist items:', error);
      toast.showError(`Failed to reorder checklist items: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

   // Function to update task status
   const handleStatusUpdate = async (newStatus: string) => {
     if (!tasks || tasks.length === 0) return;
     
     try {
       const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
       // Convert "Closed" status to "Close" for API
       const apiStatus = newStatus === 'Closed' ? 'Close' : newStatus;
       const response = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.TASKS.UPDATE_STATUS(tasks[0].id)}`, {
         method: 'POST',
         headers: {
           'Content-Type': 'application/json',
           'accept': 'application/json',
           ...(token ? { 'Authorization': `Bearer ${token}` } : {})
         },
         body: JSON.stringify({ 
           status: apiStatus,
           comments: formData.statusComment || ''
         })
       });
       
       if (response.ok) {
         toast.showSuccess(`Task status updated to ${newStatus}`);
         
         // Call the tasks API to get the latest data
         try {
           const tasksResponse = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.TASKS.LIST}?thread_id=${threadId}`, {
             method: 'GET',
             headers: {
               'accept': 'application/json',
               ...(token ? { 'Authorization': `Bearer ${token}` } : {})
             }
           });
           
           if (tasksResponse.ok) {
             const tasksData = await tasksResponse.json();
             // Update the tasks data if needed
             if (onTaskAssigned) {
               onTaskAssigned(); // This will refresh the tasks
             }
           }
         } catch (error) {
           console.error('Failed to fetch updated tasks:', error);
         }
         
         setIsChangingStatus(false);
         setFormData(prev => ({ ...prev, statusComment: '' }));
       } else {
         throw new Error('Failed to update task status');
       }
     } catch (error) {
       console.error('Failed to update task status:', error);
       toast.showError('Failed to update task status');
     }
   };

  // Custom function to format time for task history
  const formatTaskHistoryTime = (timestamp: string): string => {
    if (!timestamp) return '';
    
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return '';
      
      const now = new Date();
      const diffInMs = now.getTime() - date.getTime();
      const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
      const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
      
      if (diffInHours < 24) {
        // Show in hours for activities within 24 hours
        if (diffInHours === 0) return 'Just now';
        if (diffInHours === 1) return '1 hour ago';
        return `${diffInHours} hours ago`;
      } else {
        // Show in days and hours for activities beyond 24 hours
        const remainingHours = diffInHours % 24;
        if (diffInDays === 1) {
          if (remainingHours === 0) return '1 day ago';
          if (remainingHours === 1) return '1 day 1 hour ago';
          return `1 day ${remainingHours} hours ago`;
        } else {
          if (remainingHours === 0) return `${diffInDays} days ago`;
          if (remainingHours === 1) return `${diffInDays} days 1 hour ago`;
          return `${diffInDays} days ${remainingHours} hours ago`;
        }
      }
    } catch (error) {
      console.error('Error formatting task history time:', error);
      return '';
    }
  };

    // Function to format time in local timezone - now uses dateUtils
  const formatLocalTimeGranular = (timestamp: string): string => {
    return formatRelativeTime(timestamp);
  };

  // Function to clean task history details - remove @unknown and other unwanted text
  const cleanTaskHistoryDetails = (details: string): string => {
    if (!details) return '';
    
    // Remove @unknown and similar unwanted text
    let cleanedDetails = details
      .replace(/@unknown/g, '') // Remove @unknown
      .replace(/@Unknown/g, '') // Remove @Unknown (case variations)
      .replace(/@UNKNOWN/g, '') // Remove @UNKNOWN
      .replace(/\s+/g, ' ') // Replace multiple spaces with single space
      .trim(); // Remove leading/trailing spaces
    
    return cleanedDetails;
  };

  // Function to validate checklist completion before closing task
  const validateChecklistBeforeClose = () => {
    if (tasks && tasks.length > 0 && tasks[0]?.checklists && tasks[0].checklists.length > 0) {
      const hasIncompleteChecklists = tasks[0].checklists.some((item: any) => !item.is_completed);
      if (hasIncompleteChecklists) {
        setChecklistError('You have open checklist items. Complete them to close the task.');
        // Auto-hide error after 7 seconds
        setTimeout(() => setChecklistError(null), 7000);
        return false;
      }
    }
    return true;
  };

  if (!open) return null;

  return (
    <>
      <style>
        {`
          select option {
            max-height: 150px;
            overflow-y: auto;
          }
          select[size] {
            max-height: 150px;
            overflow-y: auto;
          }
        `}
      </style>
         {/* Panel - theme-aware for dark mode */}
         <Box
      sx={{
         position: 'fixed',
         top: 0,
         right: 0,
         width: '350px',
         height: '100vh',
         backgroundColor: theme.palette.background.paper,
         borderLeft: `1px solid ${theme.palette.divider}`,
         boxShadow: isDark ? '-2px 0 8px rgba(0,0,0,0.4)' : '-2px 0 8px rgba(0,0,0,0.1)',
         display: 'flex',
         flexDirection: 'column',
         zIndex: 99999,
       }}
     >
      {tasksLoading && (!tasks || tasks.length === 0) ? (
        // Show loading while parent fetches tasks (e.g. when opened via deep link)
        <>
          <Box
            sx={{
              p: 2,
              borderBottom: `1px solid ${theme.palette.divider}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Assign Task
            </Typography>
            <IconButton size="small" onClick={handleClose} sx={{ color: 'text.secondary' }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', flex: 1, minHeight: 200, gap: 2 }}>
            <CircularProgress size={32} />
            <Typography color="text.secondary">Loading task data...</Typography>
          </Box>
        </>
      ) : tasks && tasks.length > 0 ? (
        // Show task management options when tasks exist
        <>
          {/* Panel Header - theme-aware */}
          <Box
            sx={{
              p: 2,
              borderBottom: `1px solid ${theme.palette.divider}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Assign Task
            </Typography>
            <IconButton
              size="small"
              onClick={handleClose}
              sx={{ color: 'text.secondary' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          
                            {/* Panel Content */}
                  <Box sx={{ flex: 1, overflow: 'auto', p: 2 }} data-panel-content>
                                                              {/* Reassign Button - Only show if user has permission */}
                     {!isReassigning && !isChangingStatus && canReassignTask() && (
                       <Box sx={{ mb: 1, display: 'flex', justifyContent: 'flex-start' }}>
                         <Button
                           variant="outlined"
                           onClick={handleReassign}
                           startIcon={<EditIcon sx={{ fontSize: 12 }} />}
                           size="small"
                           sx={{
                             borderColor: '#1976d2',
                             color: '#1976d2',
                             px: 1,
                             py: 0.25,
                             fontSize: '11px',
                             '&:hover': { 
                               borderColor: '#1565c0',
                               backgroundColor: 'rgba(25, 118, 210, 0.04)'
                             }
                           }}
                         >
                           RE-ASSIGN
                         </Button>
                       </Box>
                     )}
          
                                           {/* Reassign Form - Show when reassigning */}
                      {isReassigning && (
                        <Box sx={{ mb: 4, mt: 2 }}>
                          {/* Task Form Fields */}
                          <Box sx={{ 
                            display: 'flex', 
                            flexWrap: 'wrap', 
                            gap: '10px',
                            mb: 2,
                            '& > *': {
                              flexBasis: 'calc(50% - 5px)',
                              minWidth: 'calc(50% - 5px)'
                            },
                            '& > *[data-gridsize="12"]': {
                              flexBasis: '100%',
                              minWidth: '100%'
                            }
                          }}>
                            {taskFormConfig.fields.map(field => renderField(field))}
                          </Box>
                          
                          {/* Action Buttons */}
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                            <Button
                              onClick={() => setIsReassigning(false)}
                              sx={{
                                color: 'text.secondary',
                                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                              }}
                            >
                              Cancel
                            </Button>
                            <Button
                              onClick={handleReassignSubmit}
                              variant="contained"
                              disabled={isSubmitting || !formData.taskName?.trim() || !formData.assignTo}
                              sx={{
                                backgroundColor: '#1976d2',
                                '&:hover': { backgroundColor: '#1565c0' }
                              }}
                            >
                              {isSubmitting ? (
                                <>
                                  <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} />
                                  Assigning...
                                </>
                              ) : (
                                'Assign'
                              )}
                            </Button>
                          </Box>
                        </Box>
                      )}

                                                                                       {/* Checklist Section - Display actual checklist items from API */}
                       {tasks[0]?.checklists && tasks[0].checklists.length > 0 && !isReassigning && !isChangingStatus && (
                         <Box sx={{ mb: 3, mt: 3 }}>
                           <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, color: 'text.primary' }}>
                             Checklist
                           </Typography>
                           {tasks[0].checklists.map((item: any, index: number) => (
                             <Box 
                               key={item.id || index}
                               sx={{ 
                                 display: 'flex', 
                                 alignItems: 'center', 
                                 gap: 1, 
                                 mb: 1,
                                 p: 1,
                                 border: `1px solid ${theme.palette.divider}`,
                                 borderRadius: 1,
                                 backgroundColor: isDark ? theme.palette.grey[800] : '#f9f9f9',
                                 cursor: 'grab',
                                 '&:active': {
                                   cursor: 'grabbing'
                                 }
                               }}
                               draggable
                               onDragStart={(e) => {
                                 e.dataTransfer.setData('text/plain', index.toString());
                                 e.dataTransfer.effectAllowed = 'move';
                               }}
                               onDragOver={(e) => {
                                 e.preventDefault();
                                 e.dataTransfer.dropEffect = 'move';
                               }}
                               onDrop={(e) => {
                                 e.preventDefault();
                                 const draggedIndex = parseInt(e.dataTransfer.getData('text/plain'));
                                 if (draggedIndex !== index) {
                                   const newChecklists = [...tasks[0].checklists];
                                   const [draggedItem] = newChecklists.splice(draggedIndex, 1);
                                   newChecklists.splice(index, 0, draggedItem);
                                   
                                   // Call the reorder API
                                   handleReorderChecklists(newChecklists);
                                 }
                               }}
                             >
                               <DragIndicatorIcon sx={{ color: 'text.secondary', fontSize: 16, cursor: 'grab' }} />
                               <Box
                                 sx={{
                                   display: 'flex',
                                   alignItems: 'center',
                                   gap: 1,
                                   flex: 1
                                 }}
                               >
                                 <Box
                                   onClick={() => handleChecklistToggle(item)}
                                   sx={{
                                     width: 16,
                                     height: 16,
                                     border: '2px solid #1976d2',
                                     borderRadius: '2px',
                                     backgroundColor: item.is_completed ? '#1976d2' : 'transparent',
                                     display: 'flex',
                                     alignItems: 'center',
                                     justifyContent: 'center',
                                     cursor: isCurrentUserAssignee() ? 'pointer' : 'not-allowed',
                                     opacity: isCurrentUserAssignee() ? 1 : 0.5,
                                     '&:hover': {
                                       backgroundColor: isCurrentUserAssignee() 
                                         ? (item.is_completed ? '#1565c0' : 'rgba(25, 118, 210, 0.1)')
                                         : 'transparent'
                                     }
                                   }}
                                 >
                                   {item.is_completed && (
                                     <Box
                                       sx={{
                                         width: 8,
                                         height: 8,
                                         backgroundColor: theme.palette.background.paper,
                                         clipPath: 'polygon(14% 43%, 0 65%, 50% 100%, 100% 16%, 84% 0%, 43% 62%)'
                                       }}
                                     />
                                   )}
                                 </Box>
                                 <Typography 
                                   sx={{ 
                                     flex: 1,
                                     textDecoration: item.is_completed ? 'line-through' : 'none',
                                     color: item.is_completed ? 'text.secondary' : 'text.primary',
                                     fontSize: '14px'
                                   }}
                                 >
                                   {item.text}
                                 </Typography>
                               </Box>
                               {/* Edit and Delete Icons on the right side */}
                               <Box sx={{ display: 'flex', gap: 0.5 }}>
                                 {/* Edit button - only visible if user is assignee or creator */}
                                 {canEditChecklist() && (
                                   <IconButton
                                     onClick={() => {
                                       if (item.is_completed) return; // Don't allow editing completed items
                                       handleEditChecklistItem(item);
                                     }}
                                     size="small"
                                     disabled={item.is_completed}
                                     sx={{ 
                                       color: item.is_completed ? 'text.disabled' : 'primary.main',
                                       p: 0.5,
                                       '&:hover': { 
                                         backgroundColor: item.is_completed 
                                           ? 'transparent' 
                                           : 'rgba(25, 118, 210, 0.1)' 
                                       },
                                       '&:disabled': {
                                         color: 'text.disabled'
                                       }
                                     }}
                                   >
                                     <EditIcon sx={{ fontSize: 16 }} />
                                   </IconButton>
                                 )}
                                 {/* Delete button - only visible if user is assignee or creator */}
                                 {canDeleteChecklist() && (
                                   <IconButton
                                     onClick={() => {
                                       if (item.is_completed) return; // Don't allow deleting completed items
                                       handleDeleteChecklistItem(item);
                                     }}
                                     size="small"
                                     disabled={item.is_completed}
                                     sx={{ 
                                       color: item.is_completed ? 'text.disabled' : 'error.main',
                                       p: 0.5,
                                       '&:hover': { 
                                         backgroundColor: item.is_completed 
                                           ? 'transparent' 
                                           : 'rgba(244, 67, 54, 0.1)' 
                                       },
                                       '&:disabled': {
                                         color: 'text.disabled'
                                       }
                                     }}
                                   >
                                     <DeleteIcon sx={{ fontSize: 16 }} />
                                   </IconButton>
                                 )}
                               </Box>
                             </Box>
                           ))}
                           
                           {/* Edit Input Field - Show when editing a checklist item */}
                           {showChecklistInput && editingChecklistIndex && (
                             <Box>
                               <TextField
                                 fullWidth
                                 placeholder="Enter checklist item text"
                                 value={editingChecklistValue}
                                 onChange={(e) => setEditingChecklistValue(e.target.value)}
                                 onKeyPress={(e) => {
                                   if (e.key === 'Enter' && editingChecklistValue.trim()) {
                                     handleSaveEditChecklist();
                                   } else if (e.key === 'Escape') {
                                     handleCancelEditChecklist();
                                   }
                                 }}
                                 autoFocus
                                 sx={{
                                   mb: 2,
                                   '& .MuiOutlinedInput-root': {
                                     borderRadius: 1,
                                     height: '38px'
                                   },
                                   '& .MuiInputBase-input': {
                                     textAlign: 'left'
                                   }
                                 }}
                               />
                               <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                 <Button
                                   onClick={handleCancelEditChecklist}
                                   size="small"
                                   sx={{
                                     color: 'text.secondary',
                                     px: 0.5,
                                     py: 0.125,
                                     fontSize: '12px',
                                     minWidth: 'auto',
                                     height: '28px',
                                     '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                                   }}
                                 >
                                   Cancel
                                 </Button>
                                 <Button
                                   onClick={handleSaveEditChecklist}
                                   variant="contained"
                                   size="small"
                                   disabled={!editingChecklistValue.trim()}
                                   sx={{
                                     backgroundColor: '#1976d2',
                                     px: 0.5,
                                     py: 0.125,
                                     fontSize: '12px',
                                     minWidth: 'auto',
                                     height: '28px',
                                     '&:hover': { backgroundColor: '#1565c0' }
                                   }}
                                 >
                                   Update
                                 </Button>
                               </Box>
                             </Box>
                           )}
                           
                           {/* Checklist validation error message */}
                           {checklistError && (
                               <Box sx={{ mt: 2, p: 1, backgroundColor: isDark ? 'rgba(244, 67, 54, 0.2)' : '#ffebee', border: `1px solid ${theme.palette.error.main}`, borderRadius: 1 }}>
                               <Typography variant="body2" sx={{ color: 'error.main', fontSize: '14px' }}>
                                 {checklistError}
                               </Typography>
                             </Box>
                           )}
                         </Box>
                       )}
          
                     {/* Change Task Status Section - Only show if user is assignee and task is not closed and not changing status and not reassigning */}
                     {tasks[0]?.task_details?.status !== 'Closed' && tasks[0]?.task_details?.status !== 'Close' && !isChangingStatus && !isReassigning && canChangeTaskStatus() && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                          Change task status to?
                        </Typography>
                        
                                                 {/* Status change buttons based on current status - Now in single row on left */}
                                                   {tasks[0]?.task_details?.status === 'Open' && (
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-start' }}>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={(e) => handleChangeStatus(e)}
                                data-status="In Progress"
                                sx={{
                                  borderColor: '#367fa9',
                                  color: '#367fa9',
                                  backgroundColor: '#d6e5ee',
                                  px: 1,
                                  py: 0.25,
                                  fontSize: '11px',
                                  '&:hover': { 
                                    backgroundColor: '#367fa9 !important',
                                    color: '#fff !important'
                                  }
                                }}
                              >
                                In Progress
                              </Button>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => {
                                  if (!validateChecklistBeforeClose()) return;
                                  handleChangeStatus({ currentTarget: { getAttribute: () => 'Closed' } } as any);
                                }}
                                data-status="Closed"
                                sx={{
                                  borderColor: 'green',
                                  color: 'green',
                                  backgroundColor: '#cce6cc',
                                  px: 1,
                                  py: 0.25,
                                  fontSize: '11px',
                                  '&:hover': { 
                                    backgroundColor: 'green !important',
                                    color: '#fff !important'
                                  }
                                }}
                              >
                                Close
                              </Button>
                            </Box>
                          )}
                         
                                                   {tasks[0]?.task_details?.status === 'In Progress' && (
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-start' }}>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={(e) => handleChangeStatus(e)}
                                data-status="On Hold"
                                sx={{
                                  borderColor: '#fc1818',
                                  color: '#fc1818',
                                  backgroundColor: '#ffd0d0',
                                  px: 1,
                                  py: 0.25,
                                  fontSize: '11px',
                                  '&:hover': { 
                                    backgroundColor: '#fc1818 !important',
                                    color: '#fff !important'
                                  }
                                }}
                              >
                                On Hold
                              </Button>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => {
                                  if (!validateChecklistBeforeClose()) return;
                                  handleChangeStatus({ currentTarget: { getAttribute: () => 'Closed' } } as any);
                                }}
                                data-status="Closed"
                                sx={{
                                  borderColor: 'green',
                                  color: 'green',
                                  backgroundColor: '#cce6cc',
                                  px: 1,
                                  py: 0.25,
                                  fontSize: '11px',
                                  '&:hover': { 
                                    backgroundColor: 'green !important',
                                    color: '#fff !important'
                                  }
                                }}
                              >
                                Close
                              </Button>
                            </Box>
                          )}
                         
                                                   {tasks[0]?.task_details?.status === 'On Hold' && (
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-start' }}>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={(e) => handleChangeStatus(e)}
                                data-status="In Progress"
                                sx={{
                                  borderColor: '#367fa9',
                                  color: '#367fa9',
                                  backgroundColor: '#d6e5ee',
                                  px: 1,
                                  py: 0.25,
                                  fontSize: '11px',
                                  '&:hover': { 
                                    backgroundColor: '#367fa9 !important',
                                    color: '#fff !important'
                                  }
                                }}
                              >
                                In Progress
                              </Button>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => {
                                  if (!validateChecklistBeforeClose()) return;
                                  handleChangeStatus({ currentTarget: { getAttribute: () => 'Closed' } } as any);
                                }}
                                data-status="Closed"
                                sx={{
                                  borderColor: 'green',
                                  color: 'green',
                                  backgroundColor: '#cce6cc',
                                  px: 1,
                                  py: 0.25,
                                  fontSize: '11px',
                                  '&:hover': { 
                                    backgroundColor: 'green !important',
                                    color: '#fff !important'
                                  }
                                }}
                              >
                                Close
                              </Button>
                            </Box>
                          )}
                      </Box>
                    )}

                    {/* Status Change Comment Section - Show when changing status */}
                    {isChangingStatus && (
                      <Box sx={{ mb: 4, mt: 2 }}>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                          <span style={{ color: theme.palette.text.secondary }}>Change task status to </span>
                          <span style={{ 
                            color: formData.selectedStatus === 'In Progress' ? '#367fa9' :
                                   formData.selectedStatus === 'On Hold' ? 'rgb(252, 24, 24)' :
                                   formData.selectedStatus === 'Closed' ? 'rgb(0, 128, 0)' : theme.palette.text.secondary,
                            fontWeight: 'bold' 
                          }}>{formData.selectedStatus}</span>
                        </Typography>
                        
                        {/* Comment Box */}
                        <Box sx={{ mb: 2 }}>
                          <TextField
                            fullWidth
                            multiline
                            rows={3}
                            placeholder="Add comment (mandatory)..."
                            value={formData.statusComment || ''}
                            onChange={(e) => handleInputChange('statusComment', e.target.value)}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 1
                              }
                            }}
                          />
                        </Box>
                        
                        {/* Action Buttons - Below comment box, right side */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                          <Button
                            onClick={() => setIsChangingStatus(false)}
                            sx={{
                              px: 1,
                              py: 0.25,
                              fontSize: '11px',
                              color: 'text.secondary',
                              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            onClick={() => handleStatusUpdate(formData.selectedStatus)}
                            variant="contained"
                            disabled={!formData.statusComment?.trim()}
                            sx={{
                              px: 1,
                              py: 0.25,
                              fontSize: '11px',
                              backgroundColor: '#1976d2',
                              fontWeight: 'bold',
                              '&:hover': { backgroundColor: '#1565c0' }
                            }}
                          >
                            Submit
                          </Button>
                        </Box>
                      </Box>
                    )}

                                         {/* Task Information Display Section - Show after status change */}
                     {tasks && tasks.length > 0 && (
                         <Box sx={{ mb: 3, mt: 3 }}>
                           {/* Task Details Card with Expand/Collapse - theme-aware */}
                                                   <Box sx={{ 
                            backgroundColor: theme.palette.background.paper, 
                            borderRadius: '3px', 
                            border: `1px solid ${theme.palette.divider}`,
                            overflow: 'hidden'
                          }}>
                                                       {/* Header with Expand/Collapse */}
                            <Box 
                              sx={{ 
                                p: 1.5, 
                                backgroundColor: 'inherit',
                                borderBottom: `1px solid ${theme.palette.divider}`,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                '&:hover': {
                                  backgroundColor: isDark ? theme.palette.grey[800] : '#f5f5f5'
                                }
                              }}
                              onClick={() => setTaskDetailsExpanded(!taskDetailsExpanded)}
                            >
                              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                                Task Details
                              </Typography>
                              <Box sx={{ 
                                transform: taskDetailsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s ease',
                                color: 'text.secondary'
                              }}>
                                ▲
                              </Box>
                            </Box>
                           
                           {/* Expandable Content */}
                           {taskDetailsExpanded && (
                             <Box sx={{ p: 2 }}>
                               <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                 {/* Task Name */}
                                 <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 2 }}>
                                   <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '14px', minWidth: '80px' }}>
                                     Task Name
                                   </Typography>
                                   <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.primary', fontSize: '14px' }}>
                                     {tasks[0].task_details?.task_name || 'N/A'}
                                   </Typography>
                                 </Box>
                                 
                                                                   {/* Status - theme-aware for visibility in light and dark mode */}
                                  <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 2 }}>
                                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '14px', minWidth: '80px' }}>
                                      Status
                                    </Typography>
                                    <Box sx={{ 
                                      px: 1.5, 
                                      py: 0.25, 
                                      borderRadius: '3px',
                                      ...(isDark
                                        ? {
                                            backgroundColor: tasks[0].task_details?.status === 'Open' ? theme.palette.grey[700] :
                                                               tasks[0].task_details?.status === 'In Progress' ? theme.palette.primary.dark :
                                                               tasks[0].task_details?.status === 'On Hold' ? theme.palette.error.dark :
                                                               tasks[0].task_details?.status === 'Closed' ? theme.palette.success.dark : theme.palette.grey[700],
                                            border: `1px solid ${theme.palette.grey[600]}`,
                                            color: theme.palette.common.white
                                          }
                                        : {
                                            backgroundColor: tasks[0].task_details?.status === 'Open' ? 'rgb(219, 218, 218)' :
                                                             tasks[0].task_details?.status === 'In Progress' ? '#d6e5ee' :
                                                             tasks[0].task_details?.status === 'On Hold' ? '#ffd0d0' :
                                                             tasks[0].task_details?.status === 'Closed' ? '#cce6cc' : '#f5f5f5',
                                            border: `1px solid ${tasks[0].task_details?.status === 'Open' ? 'rgb(74, 73, 73)' :
                                                                  tasks[0].task_details?.status === 'In Progress' ? '#367fa9' :
                                                                  tasks[0].task_details?.status === 'On Hold' ? '#fc1818' :
                                                                  tasks[0].task_details?.status === 'Closed' ? 'green' : '#e0e0e0'}`
                                          }
                                      )
                                    }}>
                                      <Typography variant="body2" sx={{ 
                                        fontWeight: 'bold',
                                        ...(isDark
                                          ? { color: theme.palette.common.white, fontSize: '0.75rem' }
                                          : {
                                              color: tasks[0].task_details?.status === 'Open' ? 'rgb(74, 73, 73)' :
                                                     tasks[0].task_details?.status === 'In Progress' ? '#367fa9' :
                                                     tasks[0].task_details?.status === 'On Hold' ? '#fc1818' :
                                                     tasks[0].task_details?.status === 'Closed' ? 'green' : theme.palette.text.secondary,
                                              fontSize: '0.75rem'
                                            }
                                        )
                                      }}>
                                        {tasks[0].task_details?.status || 'N/A'}
                                      </Typography>
                                    </Box>
                                  </Box>
                                 
                                 {/* Assignee */}
                                 <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 2 }}>
                                   <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '14px', minWidth: '80px' }}>
                                     Assignee
                                   </Typography>
                                   <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                     <Box sx={{ 
                                       width: 28, 
                                       height: 28, 
                                       borderRadius: '50%', 
                                       backgroundColor: '#1976d2',
                                       display: 'flex',
                                       alignItems: 'center',
                                       justifyContent: 'center',
                                       color: 'white',
                                       fontSize: '0.875rem',
                                       fontWeight: 'bold'
                                     }}>
                                       {tasks[0].task_details?.assignee?.name?.charAt(0)?.toUpperCase() || 'U'}
                                     </Box>
                                     <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                       <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.primary', lineHeight: 1.2, fontSize: '14px' }}>
                                         {tasks[0].task_details?.assignee?.name || 'N/A'}
                                       </Typography>
                                                                               <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                         {tasks[0].task_details?.assignee?.username !== 'unknown' ? `@${tasks[0].task_details?.assignee?.username}` : ''}
                                       </Typography>
                                     </Box>
                                   </Box>
                                 </Box>
                                 
                                                                   {/* Priority */}
                                  <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 2 }}>
                                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '14px', minWidth: '80px' }}>
                                      Priority
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                                             {/* Priority Icon based on level */}
                                       {tasks[0].task_details?.priority === 'Low' && (
                                         <Box sx={{ 
                                           width: 16, 
                                           height: 16, 
                                           display: 'flex', 
                                           flexDirection: 'column', 
                                           justifyContent: 'space-between',
                                           gap: 0.5
                                         }}>
                                           <Box sx={{ 
                                             width: '100%', 
                                             height: 3, 
                                             backgroundColor: '#388e3c',
                                             borderRadius: '3px'
                                           }} />
                                           <Box sx={{ 
                                             width: '100%', 
                                             height: 3, 
                                             backgroundColor: '#388e3c',
                                             borderRadius: '3px'
                                           }} />
                                           <Box sx={{ 
                                             width: '100%', 
                                             height: 3, 
                                             backgroundColor: '#388e3c',
                                             borderRadius: '3px'
                                           }} />
                                         </Box>
                                       )}
                                       {tasks[0].task_details?.priority === 'Medium' && (
                                         <Box sx={{ 
                                           width: 16, 
                                           height: 16, 
                                           display: 'flex', 
                                           flexDirection: 'column', 
                                           justifyContent: 'space-between',
                                           gap: 0.5
                                         }}>
                                           <Box sx={{ 
                                             width: '100%', 
                                             height: 3, 
                                             backgroundColor: '#f57c00',
                                             borderRadius: '3px'
                                           }} />
                                           <Box sx={{ 
                                             width: '100%', 
                                             height: 3, 
                                             backgroundColor: '#f57c00',
                                             borderRadius: '3px'
                                           }} />
                                           <Box sx={{ 
                                             width: '100%', 
                                             height: 3, 
                                             backgroundColor: '#f57c00',
                                             borderRadius: '3px'
                                           }} />
                                         </Box>
                                       )}
                                       {tasks[0].task_details?.priority === 'High' && (
                                         <Box sx={{ 
                                           width: 16, 
                                           height: 16, 
                                           display: 'flex', 
                                           flexDirection: 'column', 
                                           justifyContent: 'space-between',
                                           gap: 0.5
                                         }}>
                                           <Box sx={{ 
                                             width: '100%', 
                                             height: 3, 
                                             backgroundColor: '#d32f2f',
                                             borderRadius: '3px'
                                           }} />
                                           <Box sx={{ 
                                             width: '100%', 
                                             height: 3, 
                                             backgroundColor: '#d32f2f',
                                             borderRadius: '3px'
                                           }} />
                                           <Box sx={{ 
                                             width: '100%', 
                                             height: 3, 
                                             backgroundColor: '#d32f2f',
                                             borderRadius: '3px'
                                           }} />
                                         </Box>
                                       )}
                                       {tasks[0].task_details?.priority === 'Critical' && (
                                         <Box sx={{ 
                                           width: 16, 
                                           height: 16, 
                                           display: 'flex', 
                                           flexDirection: 'column', 
                                           justifyContent: 'center',
                                           alignItems: 'center',
                                           position: 'relative'
                                         }}>
                                           <Box sx={{ 
                                             width: '12px', 
                                             height: 2, 
                                             backgroundColor: '#f57c00',
                                             borderRadius: '3px',
                                             transform: 'rotate(45deg)',
                                             position: 'absolute'
                                           }} />
                                           <Box sx={{ 
                                             width: '12px', 
                                             height: 2, 
                                             backgroundColor: '#f57c00',
                                             borderRadius: '3px',
                                             transform: 'rotate(-45deg)',
                                             position: 'absolute'
                                           }} />
                                         </Box>
                                       )}
                                       {tasks[0].task_details?.priority === 'Blocker' && (
                                         <Box sx={{ 
                                           width: 16, 
                                           height: 16, 
                                           display: 'flex', 
                                           flexDirection: 'column', 
                                           justifyContent: 'center',
                                           alignItems: 'center',
                                           position: 'relative'
                                         }}>
                                           <Box sx={{ 
                                             width: '16px', 
                                             height: '16px', 
                                             backgroundColor: '#d32f2f',
                                             borderRadius: '50%',
                                             border: '2px solid #d32f2f'
                                           }} />
                                           <Box sx={{ 
                                             width: '10px', 
                                             height: 2, 
                                             backgroundColor: '#d32f2f',
                                             borderRadius: '3px',
                                             transform: 'rotate(45deg)',
                                             position: 'absolute'
                                           }} />
                                           <Box sx={{ 
                                             width: '10px', 
                                             height: 2, 
                                             backgroundColor: '#d32f2f',
                                             borderRadius: '3px',
                                             transform: 'rotate(-45deg)',
                                             position: 'absolute'
                                           }} />
                                         </Box>
                                       )}
                                      <Typography variant="body2" sx={{ 
                                        fontWeight: 'bold', 
                                        color: tasks[0].task_details?.priority === 'High' ? '#d32f2f' :
                                               tasks[0].task_details?.priority === 'Medium' ? '#f57c00' :
                                               tasks[0].task_details?.priority === 'Low' ? '#388e3c' :
                                               tasks[0].task_details?.priority === 'Critical' ? '#f57c00' :
                                               tasks[0].task_details?.priority === 'Blocker' ? '#d32f2f' : theme.palette.text.primary, 
                                        fontSize: '12px' 
                                      }}>
                                        {tasks[0].task_details?.priority || 'N/A'}
                                      </Typography>
                                    </Box>
                                  </Box>
                               </Box>
                             </Box>
                           )}
                                                   </Box>
                        </Box>
                      )}

                      {/* Task History Section - Show after Task Details */}
                      {tasks && tasks.length > 0 && tasks[0]?.task_history && tasks[0].task_history.length > 0 && (
                        <Box sx={{ mb: 3 }}>
                          {/* Task History Card with Expand/Collapse */}
                          <Box sx={{ 
                            backgroundColor: theme.palette.background.paper, 
                            borderRadius: '3px', 
                            border: `1px solid ${theme.palette.divider}`,
                            overflow: 'hidden'
                          }}>
                            {/* Header with Expand/Collapse */}
                            <Box 
                              sx={{ 
                                p: 1.5, 
                                backgroundColor: 'inherit',
                                borderBottom: `1px solid ${theme.palette.divider}`,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                '&:hover': {
                                  backgroundColor: isDark ? theme.palette.grey[800] : '#f5f5f5'
                                }
                              }}
                              onClick={() => setTaskHistoryExpanded(!taskHistoryExpanded)}
                            >
                              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                                Task History
                              </Typography>
                              <Box sx={{ 
                                transform: taskHistoryExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s ease',
                                color: 'text.secondary'
                              }}>
                                ▲
                              </Box>
                            </Box>
                            
                            {/* Expandable Content */}
                            {taskHistoryExpanded && (
                              <Box sx={{ p: 2 }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                                  {tasks[0].task_history.map((historyItem: any, index: number) => (
                                    <Box key={index}>
                                      {/* History Entry */}
                                      <Box sx={{ 
                                        display: 'flex', 
                                        alignItems: 'flex-start', 
                                        gap: 2,
                                        mb: index < tasks[0].task_history.length - 1 ? 2 : 0
                                      }}>
                                        {/* User Avatar */}
                                        <Box sx={{ 
                                          width: 32, 
                                          height: 32, 
                                          borderRadius: '50%', 
                                          backgroundColor: '#1976d2',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          color: 'white',
                                          fontSize: '0.875rem',
                                          fontWeight: 'bold',
                                          flexShrink: 0
                                        }}>
                                          {historyItem.user?.name?.charAt(0)?.toUpperCase() || 'U'}
                                        </Box>
                                        
                                        {/* History Content */}
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                                     {/* User Info and Timestamp Row */}
                                           <Box sx={{ 
                                             display: 'flex', 
                                             alignItems: 'center', 
                                             mb: 0.5
                                           }}>
                                             <Typography variant="body2" sx={{ 
                                               fontWeight: 'bold', 
                                               color: 'text.primary', 
                                               fontSize: '14px' 
                                             }}>
                                               {historyItem.user?.name || 'Unknown User'}
                                             </Typography>
                                             <Typography variant="caption" sx={{ 
                                               color: 'text.secondary', 
                                               fontSize: '0.75rem',
                                               mx: 0.5
                                             }}>
                                               |
                                             </Typography>
                                             <Typography variant="caption" sx={{ 
                                               color: 'text.secondary', 
                                               fontSize: '0.75rem' 
                                             }}>
                                                 {formatLocalTimeGranular(historyItem.timestamp)}
                                             </Typography>
                                           </Box>
                                          
                                          {/* Action Description */}
                                          <Typography variant="body2" sx={{ 
                                               color: 'text.primary', 
                                              fontSize: '14px',
                                            mb: 0.5,
                                            lineHeight: 1.4
                                          }}>
                                            {cleanTaskHistoryDetails(historyItem.details)}
                                          </Typography>
                                          
                                          {/* Priority Info (if available) */}
                                          {historyItem.metadata?.priority && (
                                            <Typography variant="body2" sx={{ 
color: 'text.secondary', 
                                        fontSize: '14px'
                                            }}>
                                              Priority: <span style={{ fontWeight: 'bold' }}>
                                                {historyItem.metadata.priority}
                                              </span>
                                            </Typography>
                                          )}
                                        </Box>
                                      </Box>
                                      
                                      {/* Divider between entries (except for last item) */}
                                      {index < tasks[0].task_history.length - 1 && (
                                        <Box sx={{ 
                                          height: '1px', 
                                          backgroundColor: theme.palette.divider, 
                                          my: 2 
                                        }} />
                                      )}
                                    </Box>
                                  ))}
                                </Box>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      )}
                   </Box>
                 </>
       ) : (
      <>
      {/* Panel Header - theme-aware (empty state) */}
      <Box
        sx={{
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          Assign Task
        </Typography>
        <IconButton
          size="small"
          onClick={handleClose}
          sx={{ color: 'text.secondary' }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
        {/* Panel Content */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }} data-panel-content>
          {/* Error Message Display - theme-aware */}
          {errorMessage && (
            <Box sx={{ 
              mb: 2, 
              p: 2, 
              backgroundColor: isDark ? 'rgba(244, 67, 54, 0.2)' : '#ffebee', 
              border: `1px solid ${theme.palette.error.main}`, 
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <Typography sx={{ color: 'error.main', fontSize: '14px', flex: 1 }}>
                {errorMessage}
              </Typography>
              <IconButton
                size="small"
                onClick={() => setErrorMessage(null)}
                sx={{ color: 'error.main' }}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          )}
          
          {/* Validation Error Message Display */}
          {validationErrors.general && (
            <Box sx={{ 
              mb: 2, 
              p: 2, 
              backgroundColor: isDark ? 'rgba(244, 67, 54, 0.2)' : '#ffebee', 
              border: `1px solid ${theme.palette.error.main}`, 
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <Typography sx={{ color: 'error.main', fontSize: '14px', flex: 1 }}>
                {validationErrors.general}
              </Typography>
              <IconButton
                size="small"
                onClick={() => setValidationErrors(prev => ({ ...prev, general: '' }))}
                sx={{ color: 'error.main' }}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          )}
          
                     <Box sx={{ 
             display: 'flex', 
             flexWrap: 'wrap', 
             gap: '10px',
             mb: 3,
             '& > *': {
               flexBasis: 'calc(50% - 5px)',
               minWidth: 'calc(50% - 5px)'
             },
             '& > *[data-gridsize="12"]': {
               flexBasis: '100%',
               minWidth: '100%'
             }
           }}>
             {taskFormConfig.fields.map(field => renderField(field))}
           </Box>
         
                   {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1, mt: 3, justifyContent: 'flex-end' }}>
           <Button
             onClick={handleClose}
             disabled={isSubmitting}
             sx={{
               color: 'text.secondary',
               '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
             }}
           >
             Cancel
           </Button>
                       <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={isSubmitting || !formData.taskName?.trim() || !formData.assignTo}
              sx={{
                backgroundColor: '#1976d2',
                '&:hover': { backgroundColor: '#1565c0' }
              }}
            >
             {isSubmitting ? (
               <>
                 <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} />
                 Assigning...
               </>
             ) : (
               'Assign'
             )}
           </Button>
         </Box>
       </Box>
       
                      
          
        </>
      )}
      
      {/* Calendar Popovers - Always Available */}
      {/* Calendar Popover for Assignment */}
      <Popover
        open={Boolean(calendarAnchorEl)}
        anchorEl={calendarAnchorEl}
        onClose={handleCalendarClose}
        anchorPosition={calendarPosition || undefined}
        anchorReference="anchorPosition"
        sx={{
          zIndex: 100000,
        }}
        PaperProps={{
          sx: {
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            borderRadius: 2,
          }
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        disablePortal={false}
        keepMounted={false}
        disableEscapeKeyDown={false}
        disableAutoFocus={true}
        disableRestoreFocus={true}
        slotProps={{
          paper: {
            onClick: (e) => e.stopPropagation()
          }
        }}
      >
        {renderCalendar()}
      </Popover>
      
      {/* Calendar Popover for Reassignment */}
      <Popover
        open={Boolean(reassignCalendarAnchorEl)}
        anchorEl={reassignCalendarAnchorEl}
        onClose={handleReassignCalendarClose}
        anchorPosition={reassignCalendarPosition || undefined}
        anchorReference="anchorPosition"
        sx={{
          zIndex: 100001,
        }}
        PaperProps={{
          sx: {
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            borderRadius: 2,
          }
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        disablePortal={false}
        keepMounted={false}
        disableEscapeKeyDown={false}
        disableAutoFocus={true}
        disableRestoreFocus={true}
        slotProps={{
          paper: {
            onClick: (e) => e.stopPropagation()
          }
        }}
      >
        {renderReassignCalendar()}
      </Popover>
      </Box>
    </>
  );
};

export default TaskAssignmentPanel;
